import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * Connection states for WebSocket connection retry state machine
 */
export const CONNECTION_STATE = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  READY: 'ready',
  RECONNECTING: 'reconnecting',
  ERROR: 'error',
} as const;

export const CONNECTION_STATES_BUTTON_DISABLED = [
  CONNECTION_STATE.CONNECTING,
  CONNECTION_STATE.RECONNECTING,
  CONNECTION_STATE.ERROR,
] as const;

export function isDisabledConnectionState(
  state: ConnectionState
): state is (typeof CONNECTION_STATES_BUTTON_DISABLED)[number] {
  return (CONNECTION_STATES_BUTTON_DISABLED as readonly string[]).includes(
    state
  );
}

export type ConnectionState =
  (typeof CONNECTION_STATE)[keyof typeof CONNECTION_STATE];

interface ConnectionRetryState {
  connectionState: ConnectionState;
  errorMessage?: string;

  setConnectionState: (state: ConnectionState, errorMessage?: string) => void;
  reset: () => void;
}

const initialState = {
  connectionState: CONNECTION_STATE.DISCONNECTED as ConnectionState,
  errorMessage: undefined,
};

/**
 * Zustand store for managing WebSocket connection retry state
 * Used by useConnectionRetry hook when FEATURE_FLAGS.CONNECTION_RETRY is enabled
 */
export const useConnectionRetryStore = create<ConnectionRetryState>()(
  devtools(
    (set) => ({
      ...initialState,

      setConnectionState: (state: ConnectionState, errorMessage?: string) =>
        set(
          { connectionState: state, errorMessage },
          false,
          'connectionRetry/setConnectionState'
        ),

      reset: () => set(initialState, false, 'connectionRetry/reset'),
    }),
    { name: 'connection-retry-store' }
  )
);
